import { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import { Button } from './Button';
import { Badge } from './Badge';
import { Modal } from './Modal';
import { apiFilesService, type PteroFileNode } from '@/deploy/services/api-files.service';
import type { ApiInstance, ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import { API_ENV_GROUPS, apiGroupLabel, canonicalApiEnv } from '@/deploy/utils/apiEnvs';
import { DEFAULT_INSTANCE, instanceDisplayName } from '@/deploy/utils/apiInstances';
import styles from './ConsoleModal.module.scss';

interface Props {
  projectId: string;
  api: ApiService;
  /** Env de l'onglet courant ; dev et beta ouvrent le même serveur (groupe Dev + Bêta). */
  initialEnv?: ApiEnvId;
  /** Instance dont on ouvre le serveur ; absente = Principale. */
  instance?: Pick<ApiInstance, 'key' | 'label'>;
  onClose: () => void;
}

/**
 * File explorer read/write pour une API sur un env donné (Ptero passthrough).
 * Similaire au ContainerFilesModal mais sans git (Ptero est source de vérité).
 */
export function ApiFilesModal({ projectId, api, initialEnv, instance, onClose }: Props) {
  // Toujours un env canonique : le select ne propose qu'une entrée pour Dev + Bêta.
  const [env, setEnv] = useState<ApiEnvId>(canonicalApiEnv(initialEnv ?? 'preprod'));
  const [cwd, setCwd] = useState('/');
  const [entries, setEntries] = useState<PteroFileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<PteroFileNode | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const [newFileFor, setNewFileFor] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [newFolderFor, setNewFolderFor] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PteroFileNode | null>(null);
  const inst = instance?.key ?? DEFAULT_INSTANCE;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected(null);
    setContent(null);
    try {
      const list = await apiFilesService.list(projectId, api.id, env, cwd, inst);
      list.sort((a, b) => {
        if (a.is_file !== b.is_file) return a.is_file ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
      setEntries(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [projectId, api.id, env, cwd, inst]);

  useEffect(() => { void reload(); }, [reload]);

  const openEntry = async (n: PteroFileNode) => {
    if (!n.is_file) {
      setCwd(cwd === '/' ? '/' + n.name : cwd + '/' + n.name);
      return;
    }
    setSelected(n);
    setEditing(false);
    setContent(null);
    if (n.size > 1024 * 1024) {
      setContent(null);
      setError('Fichier > 1MB — édition inline désactivée');
      return;
    }
    setContentLoading(true);
    try {
      const c = await apiFilesService.read(projectId, api.id, env, joinPath(cwd, n.name), inst);
      setContent(c);
      setEditContent(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lecture');
    } finally {
      setContentLoading(false);
    }
  };

  const goUp = () => {
    if (cwd === '/' || !cwd) return;
    const p = cwd.split('/').filter(Boolean);
    p.pop();
    setCwd(p.length === 0 ? '/' : '/' + p.join('/'));
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiFilesService.write(projectId, api.id, env, joinPath(cwd, selected.name), editContent, inst);
      setContent(editContent);
      setEditing(false);
      await reload();
    } catch (e) {
      alert('Sauvegarde ko : ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSaving(false);
    }
  };

  const confirmCreateFile = async () => {
    if (!newFileFor || !newFileName.trim()) return;
    const clean = newFileName.trim().replace(/\//g, '-');
    setSaving(true);
    try {
      await apiFilesService.write(projectId, api.id, env, joinPath(newFileFor, clean), '', inst);
      setNewFileFor(null);
      setNewFileName('');
      await reload();
    } catch (e) {
      alert('Création ko : ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSaving(false);
    }
  };

  const confirmCreateFolder = async () => {
    if (!newFolderFor || !newFolderName.trim()) return;
    const clean = newFolderName.trim().replace(/\//g, '-');
    setSaving(true);
    try {
      await apiFilesService.createFolder(projectId, api.id, env, newFolderFor, clean, inst);
      setNewFolderFor(null);
      setNewFolderName('');
      await reload();
    } catch (e) {
      alert('Création ko : ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await apiFilesService.deletePaths(projectId, api.id, env, cwd, [deleteTarget.name], inst);
      setDeleteTarget(null);
      await reload();
    } catch (e) {
      alert('Suppression ko : ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.backdrop} onMouseDown={onClose} role="dialog" aria-modal>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.chip} style={{ background: 'var(--color-primary)' }}>
              <Icon name="zap" size={14} />
            </span>
            <div className={styles.headerText}>
              <div className={styles.headerTitle}>
                {instance ? instanceDisplayName(api, instance) : api.name} · fichiers Pterodactyl
                <Badge tone="info">{apiGroupLabel(env)}</Badge>
              </div>
              <div className={styles.headerSub}>{api.repository}</div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <select value={env} onChange={(e) => setEnv(e.target.value as ApiEnvId)}
              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
              {API_ENV_GROUPS.map((e) => <option key={e} value={e}>{apiGroupLabel(e)}</option>)}
            </select>
            <button className={styles.iconBtn} onClick={() => void reload()} title="Recharger" disabled={loading}>
              <Icon name="refresh" size={12} />
            </button>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Fermer">
              <Icon name="x" size={16} />
            </button>
          </div>
        </header>

        {error && (
          <div style={{ padding: 12, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className={styles.filesLayout}>
          <aside className={styles.treePane}>
            <div className={styles.treeToolbar}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1, minWidth: 0 }}>
                <button className={styles.iconBtn} onClick={goUp} disabled={cwd === '/' || loading} title="Dossier parent">
                  <Icon name="chevron-right" size={12} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <code style={{ fontSize: 11, color: 'var(--color-text-muted)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cwd}>
                  {cwd}
                </code>
              </div>
              <div className={styles.treeToolbarActions}>
                <button className={styles.iconBtn} onClick={() => { setNewFileFor(cwd); setNewFileName(''); }}
                  title="Nouveau fichier" disabled={saving}>
                  <Icon name="git-commit" size={12} />
                </button>
                <button className={styles.iconBtn} onClick={() => { setNewFolderFor(cwd); setNewFolderName(''); }}
                  title="Nouveau dossier" disabled={saving}>
                  <Icon name="plus" size={12} />
                </button>
              </div>
            </div>

            <div className={styles.tree}>
              {loading && <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-muted)' }}>Chargement…</div>}
              {!loading && entries.length === 0 && (
                <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-muted)' }}>Dossier vide</div>
              )}
              {entries.map((n) => (
                <button key={n.name}
                  className={[styles.treeRow, selected?.name === n.name && styles.treeSelected].filter(Boolean).join(' ')}
                  style={{ paddingLeft: 8 }}
                  onClick={() => void openEntry(n)}>
                  <Icon name={n.is_file ? 'git-commit' : 'layers'} size={11} />
                  <span className={styles.treeLabel}>{n.name}</span>
                  {n.is_file && <span className={styles.treeSize}>{formatBytes(n.size)}</span>}
                </button>
              ))}
            </div>
          </aside>

          <section className={styles.fileView}>
            {selected ? (
              <>
                <header className={styles.fileHeader}>
                  <div className={styles.fileHeaderLeft}>
                    <Icon name="git-commit" size={14} />
                    <div>
                      <div className={styles.fileName}>{selected.name}</div>
                      <div className={styles.fileMeta}>{formatBytes(selected.size)}</div>
                    </div>
                  </div>
                  <div className={styles.fileHeaderActions}>
                    {!editing && content !== null && (
                      <button className={styles.actionBtn} onClick={() => setEditing(true)}>
                        <Icon name="settings" size={11} /> Éditer
                      </button>
                    )}
                    {editing && (
                      <>
                        <button className={styles.actionBtn} onClick={() => { setEditing(false); setEditContent(content ?? ''); }} disabled={saving}>
                          <Icon name="x" size={11} /> Annuler
                        </button>
                        <button className={[styles.actionBtn, styles.actionPrimary].join(' ')} onClick={saveEdit} disabled={saving}>
                          <Icon name="check" size={11} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
                        </button>
                      </>
                    )}
                    <button className={[styles.actionBtn, styles.actionDanger].join(' ')} onClick={() => setDeleteTarget(selected)} disabled={saving}>
                      <Icon name="x" size={11} /> Supprimer
                    </button>
                  </div>
                </header>
                {contentLoading ? (
                  <div className={styles.fileEmpty}>Chargement…</div>
                ) : content === null ? (
                  <div className={styles.fileEmpty}>
                    <Icon name="warn" size={20} />
                    <span>Fichier binaire ou &gt;1MB — édition inline non disponible.</span>
                  </div>
                ) : editing ? (
                  <textarea className={styles.fileEditor} value={editContent}
                    onChange={(e) => setEditContent(e.target.value)} autoFocus spellCheck={false} />
                ) : (
                  <pre className={styles.fileContent}>{content}</pre>
                )}
              </>
            ) : (
              <div className={styles.fileEmpty}>
                <Icon name="layers" size={20} />
                <span>Sélectionne un fichier ou navigue dans les dossiers</span>
              </div>
            )}
          </section>
        </div>
      </div>

      {newFileFor && (
        <Modal open onClose={() => setNewFileFor(null)}
          title="Nouveau fichier"
          subtitle={`Dans ${newFileFor}`}
          width="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setNewFileFor(null)}>Annuler</Button>
            <Button variant="primary" onClick={confirmCreateFile} disabled={!newFileName.trim() || saving}>Créer</Button>
          </>}>
          <input type="text" value={newFileName} onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void confirmCreateFile(); }}
            autoFocus placeholder="config.json"
            style={{ width: '100%', background: 'var(--color-background-secondary)', border: '1px solid var(--color-border)',
              borderRadius: 6, padding: '10px 12px', fontSize: 14, fontFamily: 'var(--font-family-mono)', color: 'var(--color-text)' }} />
        </Modal>
      )}

      {newFolderFor && (
        <Modal open onClose={() => setNewFolderFor(null)}
          title="Nouveau dossier"
          subtitle={`Dans ${newFolderFor}`}
          width="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setNewFolderFor(null)}>Annuler</Button>
            <Button variant="primary" onClick={confirmCreateFolder} disabled={!newFolderName.trim() || saving}>Créer</Button>
          </>}>
          <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void confirmCreateFolder(); }}
            autoFocus placeholder="logs"
            style={{ width: '100%', background: 'var(--color-background-secondary)', border: '1px solid var(--color-border)',
              borderRadius: 6, padding: '10px 12px', fontSize: 14, fontFamily: 'var(--font-family-mono)', color: 'var(--color-text)' }} />
        </Modal>
      )}

      {deleteTarget && (
        <Modal open onClose={() => setDeleteTarget(null)}
          title="Supprimer"
          subtitle={joinPath(cwd, deleteTarget.name)}
          width="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={saving}>Supprimer</Button>
          </>}>
          <div style={{ fontSize: 14 }}>Confirmer la suppression de <strong>{deleteTarget.name}</strong> ? Cette action est définitive côté serveur Ptero.</div>
        </Modal>
      )}
    </div>
  );
}

function joinPath(dir: string, name: string): string {
  if (dir === '/' || dir === '') return '/' + name;
  return dir + '/' + name;
}

function formatBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
  return (n / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}
